import React from 'react';

import { CustomOfferFormValues } from '@/typings/formValues';
import { Control, Controller, UseFormRegister, useWatch } from 'react-hook-form';

import { Checkbox, FormControlLabel, TextField } from '@mui/material';
import Grid from '@mui/material/Grid';

import DateWidget from '~/components/Common/Elements/DateWidget';

import { PaymentFlowV2FieldErrorMap } from '../paymentFlowUtils';

type Props = {
  control: Control<CustomOfferFormValues>;
  register: UseFormRegister<CustomOfferFormValues>;
  paymentFlowV2Errors: PaymentFlowV2FieldErrorMap | undefined;
  hasOrders?: boolean;
  isSubmitting: boolean;
  isV2FullPayment: boolean;
  isV2PaymentSchedule: boolean;
  showV2ManualFields: boolean;
};

export default function V2CustomerPaymentFields({
  control,
  register,
  paymentFlowV2Errors,
  hasOrders,
  isSubmitting,
  isV2FullPayment,
  isV2PaymentSchedule,
  showV2ManualFields,
}: Props) {
  const balanceAmount = useWatch({ control, name: 'payment_metadata.payment_flow_v2.balance_amount' });
  const manualBalanceAmount = useWatch({ control, name: 'payment_metadata.payment_flow_v2.manual_balance_amount' });

  // LEBT custom offer service fee
  const serviceFeeAmount = useWatch({ control, name: 'payment_metadata.lebt_service_fee.amount' }) ?? 0;

  const balanceWithFee = Number(balanceAmount ?? 0) + Number(serviceFeeAmount);
  const manualBalanceWithFee = Number(manualBalanceAmount ?? 0) + Number(serviceFeeAmount);

  return (
    <>
      {isV2FullPayment && (
        <Grid mt={2} container columns={6} spacing={2}>
          <Grid size={{ xs: 3, md: 1 }}>
            <TextField
              value={balanceWithFee}
              disabled
              label="Balance amount"
              type="number"
              fullWidth
              data-testid="customer-balance-amount"
            />
          </Grid>
          <Grid size={{ xs: 3, md: 1 }}>
            <Controller
              name="payment_metadata.payment_flow_v2.payment_deadline"
              control={control}
              render={({ field: { ref: _, ...field }, fieldState: { error } }) => (
                <DateWidget
                  {...field}
                  slotProps={{ textField: { helperText: error?.message, error: !!error } }}
                  disabled={hasOrders || isSubmitting}
                  label="Payment deadline"
                  disablePast
                  data-testid="payment-deadline"
                />
              )}
            />
          </Grid>
        </Grid>
      )}

      {isV2PaymentSchedule && (
        <Grid mt={2} container columns={6} spacing={2}>
          <Grid size={{ xs: 3, md: 1 }}>
            <TextField
              {...register('payment_metadata.payment_flow_v2.deposit_amount')}
              disabled={hasOrders || isSubmitting}
              label="Deposit amount"
              type="number"
              inputProps={{ step: 0.01 }}
              fullWidth
              data-testid="customer-deposit-amount"
            />
          </Grid>
          <Grid size={{ xs: 3, md: 1 }}>
            <TextField
              value={balanceWithFee}
              disabled
              label="Balance amount"
              type="number"
              fullWidth
              data-testid="customer-balance-amount"
            />
          </Grid>
          <Grid size={{ xs: 3, md: 1 }}>
            <Controller
              name="payment_metadata.payment_flow_v2.payment_deadline"
              control={control}
              render={({ field: { ref: _, ...field }, fieldState: { error } }) => (
                <DateWidget
                  {...field}
                  slotProps={{ textField: { helperText: error?.message, error: !!error } }}
                  disabled={hasOrders || isSubmitting}
                  label="Payment deadline"
                  disablePast
                  data-testid="payment-deadline"
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 3, md: 1 }}>
            <Controller
              name="payment_metadata.payment_flow_v2.balance_due_date"
              control={control}
              render={({ field: { ref: _, ...field }, fieldState: { error } }) => (
                <DateWidget
                  {...field}
                  slotProps={{ textField: { helperText: error?.message, error: !!error } }}
                  disabled={hasOrders || isSubmitting}
                  label="Final payment due date"
                  disablePast
                  data-testid="final-payment-due-date"
                />
              )}
            />
          </Grid>
        </Grid>
      )}

      {showV2ManualFields && (
        <Grid mt={2} container columns={6} spacing={2}>
          <Grid size={{ xs: 3, md: 1 }}>
            <TextField
              {...register('payment_metadata.payment_flow_v2.manual_deposit_amount')}
              helperText={paymentFlowV2Errors?.manual_deposit_amount?.message}
              error={!!paymentFlowV2Errors?.manual_deposit_amount}
              disabled={hasOrders || isSubmitting}
              label="Manual deposit amount"
              type="number"
              inputProps={{ step: 0.01 }}
              fullWidth
              data-testid="customer-deposit-amount"
            />
          </Grid>
          <Grid size={{ xs: 3, md: 1 }}>
            <TextField
              value={manualBalanceWithFee}
              helperText={paymentFlowV2Errors?.manual_balance_amount?.message}
              error={!!paymentFlowV2Errors?.manual_balance_amount}
              disabled
              label="Balance amount"
              type="number"
              fullWidth
              data-testid="customer-balance-amount"
            />
          </Grid>
          <Grid size={{ xs: 3, md: 1 }}>
            <Controller
              name="payment_metadata.payment_flow_v2.manual_deposit_due_date"
              control={control}
              render={({ field: { ref: _, ...field }, fieldState: { error } }) => (
                <DateWidget
                  {...field}
                  slotProps={{ textField: { helperText: error?.message, error: !!error } }}
                  disabled={hasOrders || isSubmitting}
                  label="Manual deposit due date"
                  disablePast
                  data-testid="customer-deposit-due-date"
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 3, md: 1 }}>
            <Controller
              name="payment_metadata.payment_flow_v2.manual_balance_due_date"
              control={control}
              render={({ field: { ref: _, ...field }, fieldState: { error } }) => (
                <DateWidget
                  {...field}
                  slotProps={{ textField: { helperText: error?.message, error: !!error } }}
                  disabled={hasOrders || isSubmitting}
                  label="Balance due date"
                  disablePast
                  data-testid="final-payment-due-date"
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 3, md: 1 }}>
            <Controller
              name="payment_metadata.payment_flow_v2.manual_deposit_pi_number"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  helperText={error?.message}
                  error={!!error}
                  disabled={isSubmitting}
                  label="Manual deposit PI number"
                  fullWidth
                  type="text"
                  data-testid="deposit-pi-number"
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 3, md: 1 }}>
            <Controller
              name="payment_metadata.payment_flow_v2.manual_deposit_payment_made"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox {...field} checked={field.value} data-testid="customer-deposit-payment-made" />}
                  label="Manual deposit payment made"
                />
              )}
            />
          </Grid>
        </Grid>
      )}
    </>
  );
}
